import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Languages } from "lucide-react";
import { PHONE, whatsappLink } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang, toggle } = useLang();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/inventory", label: t("nav.inventory") },
    { to: "/#loan", label: t("nav.loans") },
    { to: "/#about", label: t("nav.about") },
  ];

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <button onClick={() => go("/")} className="flex flex-col leading-none text-left" data-testid="nav-logo">
          <span className="font-display font-black text-lg sm:text-xl tracking-tight text-white">
            SHREE <span className="text-[#FF3B30]">PETEANJANEYA</span>
          </span>
          <span className="text-[10px] tracking-[0.25em] uppercase text-gray-400 font-bold">Swamy Enterprises · Est. 2020</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.to}
              onClick={() => go(l.to)}
              className="text-sm font-semibold text-gray-300 hover:text-white transition-colors"
              data-testid={`nav-link-${l.to}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button onClick={toggle} className="flex items-center gap-1.5 text-sm font-bold text-white border border-white/20 rounded-full px-3 py-1.5 hover:border-[#FF3B30] transition-colors" data-testid="lang-toggle">
            <Languages size={15} /> {lang === "en" ? "ಕನ್ನಡ" : "EN"}
          </button>
          <a href={`tel:${PHONE}`} className="flex items-center gap-2 text-sm font-semibold text-white hover:text-[#FF3B30] transition-colors" data-testid="nav-call">
            <Phone size={16} /> {PHONE}
          </a>
          <a
            href={whatsappLink("Hi, I'm interested in your used cars.")}
            target="_blank"
            rel="noreferrer"
            className="bg-[#25D366] hover:bg-[#1eb858] text-black font-bold text-sm px-4 py-2 rounded-md transition-colors"
            data-testid="nav-whatsapp"
          >
            {t("nav.whatsapp")}
          </a>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <button onClick={toggle} className="flex items-center gap-1 text-xs font-bold text-white border border-white/20 rounded-full px-2.5 py-1.5" data-testid="lang-toggle-mobile">
            <Languages size={14} /> {lang === "en" ? "ಕ" : "EN"}
          </button>
          <button className="text-white" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-black/95 border-t border-white/10 px-5 py-4 flex flex-col gap-4" data-testid="mobile-menu">
          {links.map((l) => (
            <button key={l.to} onClick={() => go(l.to)} className="text-left text-base font-semibold text-gray-200">
              {l.label}
            </button>
          ))}
          <a href={`tel:${PHONE}`} className="text-base font-semibold text-white flex items-center gap-2"><Phone size={16} /> {PHONE}</a>
          <a href={whatsappLink("Hi, I'm interested in your used cars.")} target="_blank" rel="noreferrer" className="bg-[#25D366] text-black font-bold text-center py-2 rounded-md">{t("nav.whatsapp")}</a>
        </div>
      )}
    </header>
  );
};
